import React, { useMemo, useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Definizione delle tipologie di lezione (usate come suggerimenti)
export const SPORT_TYPES = [
  { id: 'pattinaggio_singolo', label: 'Pattinaggio singolo' },
  { id: 'pattinaggio_coppia', label: 'Pattinaggio coppia' },
  { id: 'pattinaggio_solo_dance', label: 'Pattinaggio Solo Dance' },
  { id: 'pattinaggio_gruppo', label: 'Pattinaggio Gruppo Spettacolo' },
  { id: 'ginnastica_artistica', label: 'Ginnastica Artistica' },
  { id: 'acrobatica_aerea', label: 'Acrobatica Aerea' },
  { id: 'preparazione_atletica', label: 'Preparazione Atletica' },
  { id: 'pesi', label: 'Pesi' },
  { id: 'body_workout', label: 'Body Workout' },
  { id: 'generale', label: 'Generale' },
];

// Emoji di default per tipologie conosciute
export const SUGGESTED_EMOJIS = {
  pattinaggio_singolo: '⛸️',
  pattinaggio_coppia: '💑',
  pattinaggio_solo_dance: '💃',
  pattinaggio_gruppo: '👯',
  ginnastica_artistica: '🤸',
  acrobatica_aerea: '🎪',
  preparazione_atletica: '🏋️',
  pesi: '🏋️‍♂️',
  body_workout: '💪',
  generale: '🏃',
};

// Funzione helper per ottenere i dettagli di uno sport
export const getSportDetails = (sportId) => {
  return SPORT_TYPES.find(s => s.id === sportId) || SPORT_TYPES[0];
};

// Funzione helper per ottenere emoji suggerita
export const getSuggestedEmoji = (sportType) => {
  return SUGGESTED_EMOJIS[sportType] || '🏃';
};

// Componente Badge per mostrare emoji + tipologia
export const SportBadge = ({ sportType, size = 'md', variant = 'pill' }) => {
  // sportType ora può essere una stringa libera
  const displayLabel = sportType || 'Generale';
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
    xl: 'text-xl px-3.5 py-1.5'
  };
  
  const variantClasses = variant === 'plain' ? 'text-white' : 'rounded-xl bg-zinc-700 text-white';
  const labelClasses = variant === 'plain' ? 'font-bold' : 'font-medium';

  return (
    <span className={`inline-flex items-center ${variantClasses} ${sizeClasses[size]}`}>
      <span className={labelClasses}>{displayLabel}</span>
    </span>
  );
};

// Componente per selezione tipologia (campo libero)
export const SportSelector = ({ 
  sportType, 
  onTypeChange, 
  suggestions = [],
  className = '' 
}) => {
  const [open, setOpen] = useState(false);
  const filteredSuggestions = useMemo(() => {
    const query = (sportType || '').trim().toLowerCase();
    const unique = Array.from(new Set(suggestions.filter(Boolean)));
    if (!query) return unique;
    return unique.filter((item) => item.toLowerCase().includes(query));
  }, [sportType, suggestions]);

  return (
    <div className={`space-y-2 ${className}`}>
      <Input
        type="text"
        value={sportType}
        onChange={(e) => onTypeChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay close to allow click on suggestion
          setTimeout(() => setOpen(false), 150);
        }}
        placeholder="Es. Pattinaggio artistico"
        className={`bg-zinc-950 border-zinc-800 rounded-xl h-11 text-sm transition-colors ${sportType ? 'border-[#ccff00] text-[#ccff00]' : ''}`}
        autoComplete="off"
      />
      {open && filteredSuggestions.length > 0 && (
        <div className="relative">
          <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-zinc-900/95 backdrop-blur-xl shadow-lg">
            {filteredSuggestions.map((item) => (
              <button
                key={item}
                type="button"
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-zinc-800"
                onClick={() => onTypeChange(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Componente icona semplice (solo emoji)
export const SportIcon = ({ sportEmoji, sportType, size = 'md' }) => {
  const emoji = sportEmoji || getSuggestedEmoji(sportType);
  
  const sizeClasses = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl'
  };
  
  return (
    <span className={sizeClasses[size]} title={getSportDetails(sportType).label}>
      {emoji}
    </span>
  );
};

export default { SPORT_TYPES, SUGGESTED_EMOJIS, getSportDetails, getSuggestedEmoji, SportBadge, SportSelector, SportIcon };
